import React, { useState, useContext, useEffect, lazy, Suspense, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Users, Share2, Star } from 'lucide-react';
import { Button } from './../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './../components/ui/card';
import axios from 'axios';
import { AuthContext } from './../components/AuthContext';
import { useUserId } from './../components/UserIdHook';
import { useAppCache } from './../context/AppCacheContext';
import { Recipe } from '../services/recipeService';
import './BlogRecipePage.scss';

// Lazy load components
const LazyIngredients = lazy(() => import('./LazyIngredients'));
const LazyInstructions = lazy(() => import('./LazyInstructions'));

// Skeleton loading component
const SkeletonLoader = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
    <div className="h-20 bg-gray-200 rounded mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
  </div>
);

const RecipeDetails: React.FC = () => {
  const { recipeId } = useParams<{ recipeId: string }>();
  const { getRecipeById, fetchFullRecipeDetails } = useAppCache();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [haveTried, setHaveTried] = useState<boolean>(false);
  const { user } = useContext(AuthContext);
  const user_id = useUserId(user) ?? '';
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!user;

  const parseJsonField = (field: string | string[]): string[] => {
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing JSON field:', error);
      return [];
    }
  };


  const fetchRecipeDetails = useCallback(async () => {
    if (!recipeId) {
      setIsLoading(false);
      return;
    }

    try {
      let fetchedRecipe = getRecipeById(recipeId);
      
      if (!fetchedRecipe || !fetchedRecipe.ingredients || !fetchedRecipe.instructions) {
        fetchedRecipe = await fetchFullRecipeDetails(recipeId);
      }

      if (fetchedRecipe) {
        // Ensure ingredients and instructions are properly parsed
        fetchedRecipe.ingredients = fetchedRecipe.ingredients
          ? parseJsonField(fetchedRecipe.ingredients)
          : [];
        console.log('fetchedRecipe ' + JSON.stringify(fetchedRecipe));
        console.log('fetchedRecipe Ingredients ' + JSON.stringify(fetchedRecipe.ingredients ));
        
        fetchedRecipe.instructions = fetchedRecipe.instructions
          ? parseJsonField(fetchedRecipe.instructions)
          : [];

        console.log('Fetched recipe from API:', fetchedRecipe);

 
      } else {
        console.error('Failed to fetch recipe from API');
      }

      setRecipe(fetchedRecipe);
      setIsLoading(false);

      if (isAuthenticated) {
        await fetchUserRatingAndTriedFlag();
      }
    } catch (error) {
      console.error('Error fetching recipe details:', error);
      setIsLoading(false);
    }
  }, [recipeId, getRecipeById, fetchFullRecipeDetails, isAuthenticated]);

  useEffect(() => {
    fetchRecipeDetails();
  }, [fetchRecipeDetails]);

  const fetchUserRatingAndTriedFlag = useCallback(async () => {
    if (!recipeId || !user_id) return;

    try {
      const ratingPayload = {
        entity: 'UserRatings',
        action: 'read',
        pk_column: 'recipe_id',
        pk_value: recipeId,
        sk_column: 'user_id',
        sk_value: user_id,
      };

      const ratingResponse = await axios.post(
        'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        ratingPayload,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const parsedRatingBody = JSON.parse(ratingResponse.data.body);
      const ratingData = parsedRatingBody?.data?.rating ? parseFloat(parsedRatingBody.data.rating) : 0;
      setRating(ratingData);

      const triedPayload = {
        entity: 'UserTriedRecipes',
        action: 'read',
        pk_column: 'recipe_id',
        pk_value: recipeId,
        sk_column: 'user_id',
        sk_value: user_id,
      };

      const triedResponse = await axios.post(
        'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        triedPayload,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const parsedBody = JSON.parse(triedResponse.data.body);
      const triedData = parsedBody?.data?.tried === 'Y';

      setHaveTried(triedData);
    } catch (error) {
      console.error('Error fetching user rating or tried flag:', error);
    }
  }, [recipeId, user_id]);

  const handleAuthenticatedAction = useCallback((action: () => void) => {
    if (isAuthenticated) {
      action();
    } else {
      if (window.confirm('You need to be logged in to perform this action. Would you like to log in now?')) {
        navigate('/login');
      }
    }
  }, [isAuthenticated, navigate]);

  const updateAverageRating = useCallback(async (newRating: number) => {
    if (!recipeId || !recipe) return;
  
    try {
      const currentRatingCount = recipe.rating_count || 0;
      const currentAverageRating = parseFloat(recipe.average_rating || '0');
      
      const newTotalRating = currentAverageRating * currentRatingCount + newRating;
      const newRatingCount = currentRatingCount + 1;
      const newAverageRating = newTotalRating / newRatingCount;
  
      const payload = {
        entity: 'RecipeTable',
        action: 'update',
        pk_column: 'recipe_id',
        pk_value: recipeId,
        columns: {
          average_rating: newAverageRating.toFixed(2),
          rating_count: newRatingCount.toString()
        }
      };
  
      await axios.post('https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
  
      setRecipe(prevRecipe => {
        if (prevRecipe) {
          return {
            ...prevRecipe,
            average_rating: newAverageRating.toFixed(2),
            rating_count: newRatingCount
          };
        }
        return prevRecipe;
      });
  
      console.log('Average rating updated successfully');
    } catch (error) {
      console.error('Error updating average rating:', error);
    }
  }, [recipeId, recipe]);

  const handleSaveRatings = useCallback((newRating: number) => {
    handleAuthenticatedAction(async () => {
      if (newRating === rating) return;
  
      const payload = {
        entity: 'UserRatings',
        action: 'upsert',
        pk_column: 'recipe_id',
        pk_value: recipeId,
        sk_column: 'user_id',
        sk_value: user_id,
        columns: { rating: newRating },
      };
  
      try {
        await axios.post('https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields', payload, {
          headers: { 'Content-Type': 'application/json' },
        });
  
        setRating(newRating);
        console.log('Rating saved successfully:', newRating);
  
        await updateAverageRating(newRating);
      } catch (error) {
        console.error('Error saving rating:', error);
      }
    });
  }, [handleAuthenticatedAction, rating, recipeId, user_id, updateAverageRating]);

  const handleSaveTriedFlag = useCallback(() => {
    handleAuthenticatedAction(async () => {
      const payload = {
        entity: 'UserTriedRecipes',
        action: 'upsert',
        pk_column: 'recipe_id',
        pk_value: recipeId,
        sk_column: 'user_id',
        sk_value: user_id,
        columns: { tried: haveTried ? 'Y' : 'N' },
      };

      try {
        await axios.post('https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields', payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        console.log('Tried flag saved successfully');
      } catch (error) {
        console.error('Error saving tried flag:', error);
      }
    });
  }, [handleAuthenticatedAction, haveTried, recipeId, user_id]);

  const renderStars = useMemo(() => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={24}
        onClick={() => handleSaveRatings(index + 1)}
        fill={index < rating ? '#4CAF50' : 'none'}
        stroke={index < rating ? '#4CAF50' : '#ddd'}
        style={{ cursor: 'pointer' }}
      />
    ));
  }, [rating, handleSaveRatings]);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({
          title: recipe?.recipe_name,
          text: recipe?.description,
          url: window.location.href,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  }, [recipe]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!recipe) {
    return <div>Recipe not found</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="recipe-details-container"
    >
      <Card id="recipe-card" className="recipe-card">
        <CardHeader className="recipe-header">
          <div className="flex justify-between items-center">
            <CardTitle className="recipe-title mb-4 sm:mb-0">{recipe.recipe_name}</CardTitle>
            <Button
              variant="outline"
              className="share-button"
              onClick={handleShare}
              style={{ background: 'linear-gradient(90deg, #8B5CF6 0%, #3B82F6 100%)', color: 'white', border: 'none' }}
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          </div>
          <p className="recipe-description">{recipe.description}</p>
          <div className="recipe-meta">
            <div className="meta-item">
              <Clock className="h-4 w-4" />
              <span>
                {recipe.prep_time ?? '0'} + {recipe.cook_time ?? '0'} min
              </span>
            </div>
            <div className="meta-item">
              <Users className="h-4 w-4" />
              <span>{recipe.servings} servings</span>
            </div>
            <div className="meta-item">
              <span>
                ★ {recipe.average_rating ?? 'N/A'} ({recipe.rating_count ?? 0})
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="recipe-content">
          <Suspense fallback={<SkeletonLoader />}>
            <LazyIngredients ingredients={recipe.ingredients || []} />
          </Suspense>
          <Suspense fallback={<SkeletonLoader />}>
            <LazyInstructions instructions={recipe.instructions || []} />
          </Suspense>
        </CardContent>

        <div className="recipe-actions">
          <div className="rating-section">
            <label>Rate this recipe:</label>
            <div style={{ display: 'flex', gap: '5px' }}>{renderStars}</div>
          </div>
          <div className="tried-section">
            <label>Have you tried this recipe?</label>
            <input
              type="checkbox"
              id="tried"
              name="tried"
              checked={haveTried}
              onChange={() => handleAuthenticatedAction(() => setHaveTried(!haveTried))}
            />
            <Button onClick={handleSaveTriedFlag}>Submit Tried Status</Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default React.memo(RecipeDetails);